import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, RotateCcw, Moon, Zap, Target, Brain } from "lucide-react";
import { toast } from "sonner";
import GlassCard from "@/components/GlassCard";
import NeonButton from "@/components/NeonButton";
import CrystalIcon from "@/components/CrystalIcon";
import FrequencyGenerator from "@/components/FrequencyGenerator";
import BackgroundSounds from "@/components/BackgroundSounds";
import BinauralBeats from "@/components/BinauralBeats";
import IsochronicTones from "@/components/IsochronicTones";
import AudioEffects from "@/components/AudioEffects";
import Breathwork from "@/components/Breathwork";
import AudioRecorder from "@/components/AudioRecorder";
import PresetManager from "@/components/PresetManager";
import SleepMode from "@/components/SleepMode";
import SessionTimer from "@/components/SessionTimer";
import AudioExporter from "@/components/AudioExporter";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const Studio = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [masterVolume, setMasterVolume] = useState(70);
  const [activeTab, setActiveTab] = useState("frequencies");
  const [binauralActive, setBinauralActive] = useState(false);
  const [sleepModeActive, setSleepModeActive] = useState(false);
  const [currentSettings, setCurrentSettings] = useState({
    masterVolume: 70,
    oscillators: [],
    binaural: null,
    noise: null,
    effects: {}
  });
  
  const audioContextRef = useRef(null);
  const masterGainRef = useRef(null);

  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      masterGainRef.current = audioContextRef.current.createGain();
      masterGainRef.current.connect(audioContextRef.current.destination);
      masterGainRef.current.gain.setValueAtTime(masterVolume / 100, audioContextRef.current.currentTime);
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return { audioContext: audioContextRef.current, masterGain: masterGainRef.current };
  }, [masterVolume]);

  useEffect(() => {
    if (masterGainRef.current && audioContextRef.current) {
      masterGainRef.current.gain.setValueAtTime(masterVolume / 100, audioContextRef.current.currentTime);
    }
    setCurrentSettings(prev => ({ ...prev, masterVolume }));
  }, [masterVolume]);

  const togglePlay = () => {
    if (!isPlaying) {
      initAudio();
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setMasterVolume(70);
    setBinauralActive(false);
    setSleepModeActive(false);
    toast.info("Settings reset to defaults");
  };

  const handleLoadPreset = (settings) => {
    if (settings.masterVolume) setMasterVolume(settings.masterVolume);
    toast.success("Preset loaded");
  };

  const handleSleepModeActivate = () => {
    setSleepModeActive(true);
    if (!isPlaying) {
      initAudio();
      setIsPlaying(true);
    }
  };

  const handleSleepModeDeactivate = () => {
    setSleepModeActive(false);
  };

  const handleSleepPhaseChange = (phase) => {
    // Update settings based on phase
    if (phase.settings) {
      setMasterVolume(phase.settings.volume);
    }
  };

  const handleTimerEnd = () => {
    setIsPlaying(false);
    toast.success("Session complete! Audio stopped.");
  };

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div className="min-h-screen pt-20 md:pt-8 pb-32 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <p className="tagline-shimmer text-xs font-semibold uppercase mb-3" data-testid="studio-tagline">
            Expand Your Mind.
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Sound <span className="text-neon">Studio</span>
          </h1>
          <p className="text-gray-400">
            Design frequencies, layer sounds, and tune your nervous system
          </p>
        </motion.div>

        {/* Master Controls */}
        <GlassCard hover={false} className={cn("mb-6", sleepModeActive && "border-indigo-500/50")}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={togglePlay}
                data-testid="studio-play-btn"
                className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center transition-all",
                  isPlaying 
                    ? "bg-gradient-to-r from-red-500 to-orange-500 shadow-[0_0_30px_rgba(239,68,68,0.4)]"
                    : "bg-gradient-to-r from-violet-600 to-indigo-600 shadow-[0_0_30px_rgba(124,58,237,0.4)]"
                )}
              >
                {isPlaying ? <Pause size={28} className="text-white" /> : <Play size={28} className="text-white ml-1" />}
              </motion.button>
              
              <div>
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    isPlaying ? "bg-green-500 animate-pulse" : "bg-gray-600"
                  )} />
                  <span className="text-white font-medium">
                    {isPlaying ? "Playing" : "Ready"}
                  </span>
                  {sleepModeActive && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400">
                      <Moon size={10} className="inline mr-1" />
                      Sleep Mode
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">48kHz Studio Quality</span>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <Volume2 size={18} className="text-gray-400" />
                <Slider
                  value={[masterVolume]}
                  onValueChange={([v]) => setMasterVolume(v)}
                  min={0}
                  max={100}
                  step={1}
                  data-testid="studio-master-volume"
                  className="w-32"
                />
                <span className="text-sm text-gray-400 w-10">{masterVolume}%</span>
              </div>

              <NeonButton variant="ghost" size="sm" onClick={handleReset} data-testid="studio-reset">
                <RotateCcw size={16} className="mr-1" />
                Reset
              </NeonButton>
            </div>
          </div>
        </GlassCard>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Left 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="glass-card p-1 w-full justify-start overflow-x-auto flex-wrap">
                <TabsTrigger 
                  value="frequencies" 
                  data-testid="tab-frequencies"
                  className="data-[state=active]:bg-violet-600 data-[state=active]:text-white"
                >
                  Frequencies
                </TabsTrigger>
                <TabsTrigger 
                  value="binaural"
                  data-testid="tab-binaural"
                  className="data-[state=active]:bg-violet-600 data-[state=active]:text-white"
                >
                  Binaural
                </TabsTrigger>
                <TabsTrigger 
                  value="background"
                  data-testid="tab-background"
                  className="data-[state=active]:bg-violet-600 data-[state=active]:text-white"
                >
                  Background
                </TabsTrigger>
                <TabsTrigger 
                  value="effects"
                  data-testid="tab-effects"
                  className="data-[state=active]:bg-violet-600 data-[state=active]:text-white"
                >
                  Effects
                </TabsTrigger>
                <TabsTrigger 
                  value="breathwork"
                  data-testid="tab-breathwork"
                  className="data-[state=active]:bg-violet-600 data-[state=active]:text-white"
                >
                  Breathwork
                </TabsTrigger>
                <TabsTrigger 
                  value="isochronic"
                  data-testid="tab-isochronic"
                  className="data-[state=active]:bg-amber-600 data-[state=active]:text-white"
                >
                  <Zap size={14} className="mr-1" />
                  Isochronic
                </TabsTrigger>
                <TabsTrigger 
                  value="sleep"
                  data-testid="tab-sleep"
                  className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
                >
                  <Moon size={14} className="mr-1" />
                  Sleep
                </TabsTrigger>
              </TabsList>

              <TabsContent value="frequencies">
                <GlassCard hover={false}>
                  <FrequencyGenerator />
                </GlassCard>
              </TabsContent>

              <TabsContent value="binaural">
                <GlassCard hover={false}>
                  <BinauralBeats 
                    audioContext={audioContextRef.current}
                    isPlaying={isPlaying}
                  />
                </GlassCard>
              </TabsContent>

              <TabsContent value="background">
                <GlassCard hover={false}>
                  <BackgroundSounds 
                    audioContext={audioContextRef.current}
                    masterGain={masterGainRef.current}
                    isPlaying={isPlaying}
                  />
                </GlassCard>
              </TabsContent>

              <TabsContent value="effects">
                <GlassCard hover={false}>
                  <AudioEffects 
                    audioContext={audioContextRef.current}
                    masterGain={masterGainRef.current}
                    isPlaying={isPlaying}
                  />
                </GlassCard>
              </TabsContent>

              <TabsContent value="breathwork">
                <GlassCard hover={false}>
                  <Breathwork 
                    audioContext={audioContextRef.current}
                    binauralActive={binauralActive}
                  />
                </GlassCard>
              </TabsContent>

              <TabsContent value="isochronic">
                <GlassCard hover={false} className="border-amber-500/20">
                  <IsochronicTones 
                    audioContext={audioContextRef.current}
                    masterGain={masterGainRef.current}
                    isPlaying={isPlaying}
                  />
                </GlassCard>
              </TabsContent>

              <TabsContent value="sleep">
                <GlassCard hover={false} className="border-indigo-500/20">
                  <SleepMode 
                    onActivate={handleSleepModeActivate}
                    onDeactivate={handleSleepModeDeactivate}
                    onPhaseChange={handleSleepPhaseChange}
                    isActive={sleepModeActive}
                    audioContext={audioContextRef.current}
                  />
                </GlassCard>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Right column */}
          <div className="space-y-6">
            {/* Session Timer */}
            <GlassCard hover={false}>
              <SessionTimer 
                onTimerEnd={handleTimerEnd}
                onTimerStart={() => {}}
                isPlaying={isPlaying}
                audioContext={audioContextRef.current}
              />
            </GlassCard>

            {/* Audio Exporter */}
            <GlassCard hover={false}>
              <AudioExporter 
                audioContext={audioContextRef.current}
                masterGain={masterGainRef.current}
                isPlaying={isPlaying}
              />
            </GlassCard>

            {/* Audio Recorder */}
            <GlassCard hover={false}>
              <AudioRecorder 
                audioContext={audioContextRef.current}
                masterGain={masterGainRef.current}
                isPlaying={isPlaying}
              />
            </GlassCard>

            {/* Preset Manager */}
            <GlassCard hover={false}>
              <PresetManager 
                currentSettings={currentSettings}
                onLoadPreset={handleLoadPreset}
                isPlaying={isPlaying}
              />
            </GlassCard>

            {/* Quick Presets */}
            <GlassCard hover={false}>
              <h4 className="text-sm font-medium text-gray-300 mb-4">Quick Presets</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: "Deep Sleep", icon: Moon, desc: "Delta + Brown" },
                  { name: "Focus", icon: Target, desc: "Beta + White" },
                  { name: "Meditation", icon: Brain, desc: "Theta + 432Hz" },
                  { name: "Energy", icon: Zap, desc: "Gamma + 528Hz" },
                ].map((preset, i) => (
                  <motion.button
                    key={preset.name}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => toast.info(`${preset.name} preset activated`)}
                    data-testid={`quick-preset-${preset.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="crystal-card group text-center !p-4"
                  >
                    <CrystalIcon icon={preset.icon} accent={`qp${i}`} size={48} />
                    <span className="text-sm font-semibold text-white block mb-1">
                      {preset.name}
                    </span>
                    <span className="text-xs text-gray-400 block">{preset.desc}</span>
                  </motion.button>
                ))}
              </div>
            </GlassCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Studio;
